"use client";

import type React from "react";
import { useState } from "react";
import {
  Container,
  Card,
  CardBody,
  VStack,
  Text,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  Button,
  Link as ChakraLink,
  Box,
} from "@chakra-ui/react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function PasswordReset() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/set-new-password`,
      });

      if (error) throw error;

      setSuccess(true);
      navigate("/password-reset-otp", { state: { email } });
    } catch (error: any) {
      setError(error.error_description || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="md" py={12}>
      <Card>
        <CardBody p={8}>
          <VStack spacing={6}>
            <Box textAlign="center">
              <Box
                as="img"
                src="/main-logo.png"
                alt="CallSupplai Logo"
                h="60px"
                mx="auto"
                mb={2}
              />

              <Text color="secondary.600" fontSize="sm">
                Enter your{" "}
                <Text as="span" color="blue.600">
                  email address
                </Text>{" "}
                and we'll send you a verification code to reset your password
              </Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md" textColor={"red.600"}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            {success ? (
              <VStack spacing={4}>
                <Alert
                  status="success"
                  borderRadius="md"
                  textColor={"green.600"}
                >
                  <AlertIcon />
                  Password reset email sent. Please check your inbox.
                </Alert>
                <Button as={Link} to="/login" colorScheme="primary" w="100%">
                  Back to Sign In
                </Button>
              </VStack>
            ) : (
              <Box as="form" onSubmit={handlePasswordReset} w="100%">
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel color="gray.600">Email</FormLabel>
                    <Input
                      type="email"
                      placeholder="Your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      bg="gray.50"
                      _placeholder={{ color: "gray.500" }}
                    />
                  </FormControl>

                  <Button
                    variant="brandGradient"
                    type="submit"
                    size="lg"
                    w="100%"
                    isLoading={loading}
                    loadingText="Sending..."
                  >
                    Send Reset Instructions
                  </Button>

                  <ChakraLink as={Link} to="/login" color="primary.600">
                    Back to Sign In
                  </ChakraLink>
                </VStack>
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
}
